import last from 'lodash/last';
import noop from 'lodash/noop';
import {
  catchError,
  finalize,
  mergeMap,
  of,
  switchMap,
  toArray,
  windowTime,
} from 'rxjs';
import { Subscription, fromCallback } from 'xstate';

import { AppointmentFileTypesLookup } from '@eluve/graphql-types';
import { CorrelatedLogger } from '@eluve/logger';
import {
  AacHeaderBase64,
  FixedQueue,
  OpusHeaderBase64,
  base64ToBlob,
  isAudioFileChunkFile,
  makeStandardAudioFileName,
} from '@eluve/utils';

import { AudioPacket } from '../types';

import {
  AudioFileSystemEvents,
  type AudioFileSystemInput as Input,
} from './audioFileSystem.types';

const AUDIO_PACKETS_PER_SECOND = 10;

const closeFileSafe = async (
  file?: FileSystemWritableFileStream,
  fileName?: string,
  logger?: CorrelatedLogger,
  context?: string,
) => {
  try {
    if (!file) {
      if (fileName && logger) {
        logger('debug', 'No file to close (already closed or undefined)', {
          fileName,
          context: context ?? 'unknown',
        });
      }
      return;
    }

    if (fileName && logger) {
      logger('info', 'Attempting to close local audio file', {
        fileName,
        context: context ?? 'unknown',
      });
    }

    await file.close();

    if (fileName && logger) {
      logger('info', 'Successfully closed local audio file', {
        fileName,
        context: context ?? 'unknown',
      });
    }
  } catch (error) {
    if (fileName && logger) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isInvalidStateError = errorMessage.includes('InvalidStateError');

      // InvalidStateError is commonly thrown when trying to close an already-closed stream
      // This is expected in race conditions between beforeunload and normal XState cleanup
      if (isInvalidStateError && context === 'cleanup-return') {
        logger(
          'debug',
          'Expected error: tried to close already-closed file during cleanup',
          {
            fileName,
            context: context ?? 'unknown',
            error: errorMessage,
          },
        );
      } else {
        if (errorMessage?.includes('Cannot close a closed')) {
          // Closing an already-closed file - not an error
          return;
        }
        logger('error', 'Failed to close local audio file', {
          fileName,
          context: context ?? 'unknown',
          error: errorMessage,
          errorName: error instanceof Error ? error.name : 'Unknown',
          isInvalidStateError,
        });
      }
    }
  }
};

type SupportedEncoding = 'opus' | 'aac';
const opusHeaderBlob = base64ToBlob(OpusHeaderBase64);
const aacHeaderBlob = base64ToBlob(AacHeaderBase64);

const audioFileHeaders: Record<SupportedEncoding, Blob> = {
  opus: opusHeaderBlob,
  aac: aacHeaderBlob,
};

type ChunkedFile = {
  file: FileSystemWritableFileStream;
  fileName: string;
  chunkNum: number;
};

export const audioFileSystem = fromCallback<AudioFileSystemEvents, Input>(
  ({ input, sendBack, receive }) => {
    const {
      userFileSystem,
      appointmentId,
      fileExtension,
      recordingStartedAt,
      db,
      chunkedBatchTranscriptionEnabled,
      chunkDurationMinutes,
      logger,
      initialFileName,
    } = input;

    const safeSendBack = sendBack as (evt: AudioFileSystemEvents) => void;

    let writableFile: FileSystemWritableFileStream | undefined;

    const fileName = initialFileName;
    let isFileClosed = false;

    const chunkedWritableFiles: ChunkedFile[] = [];
    let audioPacketSubscription: Subscription | undefined;
    let chunkedFileSubscription: Subscription | undefined;
    const fixedQueue = new FixedQueue<AudioPacket>(3);

    const processedCompletedChunks: string[] = [];
    let removeBeforeUnloadHandler: (() => void) | undefined;

    // Emergency cleanup function for browser refresh/close scenarios
    const emergencyCleanup = async () => {
      logger(
        'warn',
        'Emergency cleanup triggered - browser refresh/close detected',
        {
          fileName,
          totalPacketsWritten: input.numAudioPacketsConsumedByLocalFile,
          totalBytesWritten: input.totalBytesConsumedByLocalFile,
          fileAlreadyClosed: isFileClosed,
        },
      );

      if (writableFile && !isFileClosed) {
        try {
          await writableFile.close();
          isFileClosed = true;
          logger('info', 'Emergency: Successfully closed main audio file', {
            fileName,
          });
        } catch (error) {
          // Even if close fails, mark as closed to prevent double-close attempts
          isFileClosed = true;
          logger('error', 'Emergency: Failed to close main audio file', {
            fileName,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      } else if (isFileClosed) {
        logger('info', 'Emergency cleanup skipped - file already closed', {
          fileName,
        });
      }

      // Close all chunked files
      for (const chunkedFile of chunkedWritableFiles) {
        try {
          await chunkedFile.file.close();
          logger('info', 'Emergency: Successfully closed chunked file', {
            fileName: chunkedFile.fileName,
          });
        } catch (error) {
          logger('error', 'Emergency: Failed to close chunked file', {
            fileName: chunkedFile.fileName,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }
    };

    const sendCompletedChunkForProcessing = async ({
      completedChunk,
    }: {
      completedChunk: ChunkedFile;
    }) => {
      if (processedCompletedChunks.includes(completedChunk.fileName)) {
        return;
      }
      await closeFileSafe(
        completedChunk.file,
        completedChunk.fileName,
        logger,
        'chunk-processing',
      );
      safeSendBack({
        type: 'fileSystem.fileReady',
        fileName: completedChunk.fileName,
        chunkNum: completedChunk.chunkNum,
      });
      processedCompletedChunks.push(completedChunk.fileName);
    };

    const initializeFileSystem = async () => {
      try {
        if (userFileSystem.type !== 'available') {
          safeSendBack({
            type: 'fileSystem.error',
            error: new Error('File system not available'),
          });
          return;
        }

        const appointmentDir =
          await userFileSystem.appointmentsDir.getDirectoryHandle(
            appointmentId,
            {
              create: true,
            },
          );

        const localFile = await appointmentDir.getFileHandle(fileName, {
          create: true,
        });

        const createNewChunkedFile = async (chunkNum: number) => {
          const newFileName = makeStandardAudioFileName({
            recordingStartedAt,
            chunkNum,
            isChunkingEnabled: true,
          });
          const newFileHandle = await appointmentDir.getFileHandle(
            newFileName,
            {
              create: true,
            },
          );
          const newFile = await newFileHandle.createWritable();

          // Always add artificial header for all chunks
          const encodingType: SupportedEncoding =
            fileExtension === 'webm' ? 'opus' : 'aac';
          await newFile.write(audioFileHeaders[encodingType]);

          for (const existingItem of fixedQueue.toArray()) {
            await newFile.write(existingItem.data);
          }

          await db.appointmentFiles.add({
            id: newFileName,
            appointmentId,
            attemptCount: 0,
            lastUploadAttemptedAt: new Date().toISOString(),
            wasDegraded: false,
            chunkNum,
            chunkDurationMinutes,
            uploadType: isAudioFileChunkFile(newFileName)
              ? AppointmentFileTypesLookup.CHUNKED_AUDIO
              : AppointmentFileTypesLookup.USER_BACKUP_AUDIO,
            shouldBeTranscribed: true,
          });

          safeSendBack({
            type: 'fileSystem.fileCreated',
            fileName: newFileName,
            chunkNum: chunkNum,
          });

          return { file: newFile, fileName: newFileName, chunkNum };
        };

        if ('createWritable' in localFile) {
          const file = await localFile.createWritable();

          // Create a file reference in the DB so we can track the status until it ultimately
          // gets flushed to our backend
          await db.appointmentFiles.add({
            id: fileName,
            appointmentId,
            attemptCount: 0,
            lastUploadAttemptedAt: new Date().toISOString(),
            wasDegraded: false,
            shouldBeTranscribed: true,
            uploadType: isAudioFileChunkFile(fileName)
              ? AppointmentFileTypesLookup.CHUNKED_AUDIO
              : AppointmentFileTypesLookup.USER_BACKUP_AUDIO,
          });

          writableFile = file;

          safeSendBack({
            type: 'fileSystem.fileCreated',
            chunkNum: 0,
            fileName,
          });

          // Register emergency cleanup for browser refresh/close
          const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            logger(
              'warn',
              'beforeunload event detected - attempting emergency file cleanup',
              {
                fileName,
                currentlyFileClosed: isFileClosed,
              },
            );
            // Note: We can't use async/await in beforeunload, but we can try synchronous close
            emergencyCleanup().catch(noop);
          };

          window.addEventListener('beforeunload', handleBeforeUnload);

          // Store cleanup function for later removal
          removeBeforeUnloadHandler = () => {
            logger('info', 'Removing beforeunload handler for audio file', {
              fileName,
            });
            window.removeEventListener('beforeunload', handleBeforeUnload);
          };

          const logAudioPacketMilestones = () => {
            logger(
              'info',
              'Num seconds of audio packets consumed by local file',
              {
                numAudioPacketsConsumedByLocalFile:
                  input.numAudioPacketsConsumedByLocalFile,
                totalBytesConsumedByLocalFile:
                  input.totalBytesConsumedByLocalFile,
                numSeconds: Math.floor(
                  input.numAudioPacketsConsumedByLocalFile / 10,
                ),
                fileName,
              },
            );
          };

          logger('info', 'Starting audio packet subscription for local file', {
            fileName,
          });

          audioPacketSubscription = input.audioPacketSubject
            .asObservable()
            .pipe(
              switchMap(async (packet) => {
                try {
                  await file.write(packet.data);
                  input.numAudioPacketsConsumedByLocalFile =
                    (input.numAudioPacketsConsumedByLocalFile ?? 0) + 1;
                  input.totalBytesConsumedByLocalFile =
                    (input.totalBytesConsumedByLocalFile ?? 0) +
                    packet.data.size;

                  if (
                    input.numAudioPacketsConsumedByLocalFile ===
                      AUDIO_PACKETS_PER_SECOND ||
                    input.numAudioPacketsConsumedByLocalFile ===
                      AUDIO_PACKETS_PER_SECOND * 30 ||
                    input.numAudioPacketsConsumedByLocalFile %
                      (AUDIO_PACKETS_PER_SECOND * 60) ===
                      0
                  ) {
                    logAudioPacketMilestones();
                  }
                } catch (writeError) {
                  logger(
                    'error',
                    'Failed to write audio packet to local file',
                    {
                      fileName,
                      packetSize: packet.data.size,
                      error:
                        writeError instanceof Error
                          ? writeError.message
                          : String(writeError),
                      totalPacketsWritten:
                        input.numAudioPacketsConsumedByLocalFile,
                    },
                  );
                  throw writeError;
                }
              }),
              catchError((e) => {
                logger('error', 'Audio file system write error', {
                  fileName,
                  error: e instanceof Error ? e.message : String(e),
                  totalPacketsWritten: input.numAudioPacketsConsumedByLocalFile,
                  totalBytesWritten: input.totalBytesConsumedByLocalFile,
                });
                safeSendBack({
                  type: 'fileSystem.error',
                  error: e,
                });
                return of(e);
              }),
              finalize(() => {
                logger(
                  'info',
                  'Audio packet subscription ended for local file',
                  {
                    fileName,
                    finalPacketCount: input.numAudioPacketsConsumedByLocalFile,
                    finalByteCount: input.totalBytesConsumedByLocalFile,
                  },
                );
              }),
            )
            .subscribe();

          if (chunkedBatchTranscriptionEnabled) {
            logger('info', 'Chunked batch transcription enabled', {
              chunkDurationMinutes,
            });
            chunkedFileSubscription = input.audioPacketSubject
              .pipe(
                windowTime(chunkDurationMinutes * 60 * 1000),
                mergeMap((window$, chunkNum) =>
                  window$.pipe(
                    mergeMap(async (packet, packetNum) => {
                      // Skip first packet of first chunk only to avoid corrupted WebM headers.
                      // MediaRecorder sometimes produces incomplete/malformed container headers
                      // in its very first data packet, causing the first chunk to be unplayable.
                      // This issue only affects chunk 0 - subsequent chunks work fine with artificial headers.
                      if (chunkNum === 0 && packetNum === 0) {
                        return;
                      }

                      // Create chunk file at the right time for each chunk:
                      // - First chunk (0): Create on packet 1 since we skipped packet 0
                      // - Other chunks (1+): Create on packet 0 as normal
                      // All chunks get artificial headers to ensure they're always playable
                      if (
                        (chunkNum === 0 && packetNum === 1) ||
                        (chunkNum > 0 && packetNum === 0)
                      ) {
                        const existingChunk = last(chunkedWritableFiles);
                        if (existingChunk) {
                          safeSendBack({
                            type: 'fileSystem.rollingToNewChunk',
                            completedChunkName: existingChunk.fileName,
                          });
                        }
                        const newFile = await createNewChunkedFile(chunkNum);
                        chunkedWritableFiles.push(newFile);
                      }
                      const size = chunkedWritableFiles.length - 1;
                      if (chunkedWritableFiles[size]) {
                        await chunkedWritableFiles[size].file.write(
                          packet.data,
                        );
                      }
                      fixedQueue.push(packet);
                    }),
                    toArray(), // wait for window to complete before closing
                    mergeMap(async () => {
                      const completedChunk =
                        chunkedWritableFiles[chunkedWritableFiles.length - 1];
                      if (completedChunk) {
                        await sendCompletedChunkForProcessing({
                          completedChunk,
                        });
                      }
                    }),
                  ),
                ),
                catchError((e) => {
                  safeSendBack({
                    type: 'fileSystem.error',
                    error: e,
                  });
                  return of(e);
                }),
              )
              .subscribe();
          }

          safeSendBack({
            type: 'fileSystem.ready',
          });
        } else {
          safeSendBack({
            type: 'fileSystem.error',
            error: new Error('Incompatible Browser. Cannot save local file'),
          });
        }
      } catch (e) {
        safeSendBack({
          type: 'fileSystem.error',
          error: e instanceof Error ? e : new Error(String(e)),
        });
      }
    };

    initializeFileSystem();

    receive((event) => {
      if (event.type === 'fileSystem.close') {
        logger('info', 'Received fileSystem.close event', {
          fileName,
          totalPacketsWritten: input.numAudioPacketsConsumedByLocalFile,
          totalBytesWritten: input.totalBytesConsumedByLocalFile,
        });

        // Remove beforeunload handler before closing
        removeBeforeUnloadHandler?.();

        if (!isFileClosed) {
          // Fire and forget - we can't await in receive handler
          closeFileSafe(writableFile, fileName, logger, 'fileSystem.close')
            .then(() => {
              isFileClosed = true;
            })
            .catch(() => {
              // Error already logged in closeFileSafe
              isFileClosed = true;
            });
        }
        chunkedWritableFiles.forEach((chunkedFile) =>
          sendCompletedChunkForProcessing({ completedChunk: chunkedFile }),
        );
      }
    });

    return () => {
      logger(
        'info',
        'Audio file system cleanup - closing files and unsubscribing',
        {
          fileName: fileName ?? 'undefined',
          numAudioPacketsConsumedByLocalFile:
            input.numAudioPacketsConsumedByLocalFile,
          totalBytesConsumedByLocalFile: input.totalBytesConsumedByLocalFile,
          chunkedFilesCount: chunkedWritableFiles.length,
        },
      );

      // Remove beforeunload handler
      removeBeforeUnloadHandler?.();

      audioPacketSubscription?.unsubscribe();
      chunkedFileSubscription?.unsubscribe();

      if (!isFileClosed) {
        logger('info', 'Closing audio file', {
          fileName: fileName ?? 'undefined',
          totalPacketsWritten: input.numAudioPacketsConsumedByLocalFile,
          totalBytesWritten: input.totalBytesConsumedByLocalFile,
        });

        // Fire and forget - return function can't be async
        closeFileSafe(writableFile, fileName, logger, 'cleanup-return')
          .then(() => {
            isFileClosed = true;
          })
          .catch(() => {
            // Error already logged in closeFileSafe
            isFileClosed = true;
          });
      } else {
        logger(
          'debug',
          'Skipping file close in cleanup - file already closed',
          {
            fileName: fileName ?? 'undefined',
            context: 'cleanup-return',
          },
        );
      }

      chunkedWritableFiles.forEach((chunkedFile) =>
        sendCompletedChunkForProcessing({ completedChunk: chunkedFile }),
      );
    };
  },
);
